import { motion } from "framer-motion";
import { FiAlertTriangle, FiArrowRight, FiFileText } from "react-icons/fi";
import { Link } from "react-router-dom";

const Section = ({ number, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="mb-10"
  >
    <div className="flex items-start gap-4 mb-4">
      <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 text-sm font-extrabold flex items-center justify-center">
        {number}
      </span>
      <h2 className="text-xl font-extrabold text-slate-900 pt-1">{title}</h2>
    </div>
    <div className="pl-12 text-[15px] text-slate-700 leading-relaxed space-y-3">
      {children}
    </div>
  </motion.div>
);

const Bullet = ({ children }) => (
  <div className="flex items-start gap-3">
    <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full mt-2 flex-shrink-0" />
    <span>{children}</span>
  </div>
);

const TermsPage = () => {
  const lastUpdated = "January 1, 2024";

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* ── Page Hero ── */}
      <div className="bg-white border-b-2 border-slate-100">
        <div className="container-main py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <FiFileText size={20} className="text-indigo-600" />
              </div>
              <span className="text-xs font-extrabold uppercase tracking-widest text-indigo-600">
                Legal Document
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-extrabold text-slate-900 font-display mb-3">
              Terms of Service
            </h1>
            <p className="text-base text-slate-500 font-semibold mb-6">
              Last updated: {lastUpdated}
            </p>
            <p className="text-base text-slate-600 leading-relaxed max-w-2xl">
              Please read these Terms of Service carefully before using
              SiteVault. By accessing or using our service, you agree to be
              bound by these terms. If you disagree with any part of these
              terms, please do not use our service.
            </p>

            {/* Highlight box */}
            <div className="mt-6 flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl max-w-2xl">
              <FiAlertTriangle
                size={18}
                className="text-amber-600 flex-shrink-0 mt-0.5"
              />
              <p className="text-sm font-semibold text-amber-800">
                By using SiteVault, you confirm that you are at least 13 years
                of age and agree to these Terms of Service and our Privacy
                Policy.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Table of Contents + Content ── */}
      <div className="container-main py-12 lg:py-16">
        <div className="grid lg:grid-cols-4 gap-10 lg:gap-12 items-start">
          {/* Sticky Table of Contents */}
          <div className="lg:sticky lg:top-24">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl border-2 border-slate-100 p-5"
            >
              <h3 className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-4">
                Contents
              </h3>
              <nav className="space-y-1">
                {[
                  "Acceptance of Terms",
                  "User Accounts",
                  "Website Submissions",
                  "Reviews & Content",
                  "Prohibited Activities",
                  "Intellectual Property",
                  "Third-Party Links",
                  "Disclaimer of Warranties",
                  "Limitation of Liability",
                  "Termination",
                  "Governing Law",
                  "Changes to Terms",
                  "Contact",
                ].map((item, i) => (
                  <a
                    key={item}
                    href={`#term-${i + 1}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
                  >
                    <span className="text-[10px] font-extrabold text-slate-300 w-5">
                      {i + 1}
                    </span>
                    {item}
                  </a>
                ))}
              </nav>
            </motion.div>
          </div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="lg:col-span-3"
          >
            <div id="term-1">
              <Section number="1" title="Acceptance of Terms">
                <p>
                  By accessing or using SiteVault ("the Service"), you agree to
                  be bound by these Terms of Service ("Terms"). These Terms
                  apply to all visitors, users, and others who access or use the
                  Service.
                </p>
                <p>
                  If you are using the Service on behalf of an organization, you
                  represent and warrant that you have the authority to bind that
                  organization to these Terms.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-2">
              <Section number="2" title="User Accounts">
                <p>
                  When you create an account, you must provide accurate,
                  complete, and current information. You are responsible for:
                </p>
                <div className="space-y-2 mt-3">
                  {[
                    "Maintaining the confidentiality of your account credentials",
                    "All activities that occur under your account",
                    "Notifying us immediately of any unauthorized access at security@sitevault.io",
                    "Ensuring your account information remains accurate and up-to-date",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
                <p className="mt-3">
                  We reserve the right to suspend or terminate accounts that
                  violate these Terms or are inactive for extended periods.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-3">
              <Section number="3" title="Website Submissions">
                <p>
                  When you submit a website to our directory, you represent and
                  warrant that:
                </p>
                <div className="space-y-2 mt-3">
                  {[
                    "The website is legitimate, active, and publicly accessible",
                    "You have the right or authorization to submit the website",
                    "The website does not violate any applicable laws or third-party rights",
                    "Your submission information is accurate and not misleading",
                    "The website does not contain malware or harmful content",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
                <div className="mt-4 p-4 bg-slate-50 rounded-xl border-2 border-slate-100">
                  <p className="text-sm font-bold text-slate-800 mb-1">
                    Our Right to Reject
                  </p>
                  <p className="text-sm text-slate-600">
                    SiteVault reserves the right to reject, remove, or modify
                    any submission at our sole discretion, without notice or
                    liability.
                  </p>
                </div>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-4">
              <Section number="4" title="Reviews & Content">
                <p>
                  By posting reviews, ratings, or other content, you grant
                  SiteVault a worldwide, non-exclusive, royalty-free license to
                  use, display, reproduce, and distribute that content on our
                  platform.
                </p>
                <p className="font-semibold text-slate-800 mt-4 mb-2">
                  You agree NOT to post content that:
                </p>
                <div className="space-y-2">
                  {[
                    "Is false, misleading, or defamatory",
                    "Contains spam or commercial solicitations",
                    "Violates any third-party intellectual property rights",
                    "Contains personally identifiable information of others",
                    "Is abusive, threatening, or harassing",
                    "Promotes illegal activities",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
                <p className="mt-3">
                  We reserve the right to remove any content that violates these
                  guidelines without notice.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-5">
              <Section number="5" title="Prohibited Activities">
                <p>
                  You agree not to engage in any of the following activities:
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  {[
                    "Violating applicable laws or regulations",
                    "Attempting unauthorized system access",
                    "Interfering with service operations",
                    "Using automated scraping tools",
                    "Impersonating another person or entity",
                    "Submitting malware or phishing sites",
                    "Manipulating ratings or reviews",
                    "Creating multiple accounts to circumvent bans",
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex items-start gap-2.5 p-3 bg-red-50 rounded-lg border border-red-100"
                    >
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4">
                  Violation of these prohibitions may result in immediate
                  account termination and potential legal action.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-6">
              <Section number="6" title="Intellectual Property">
                <p>
                  The Service and its original content, features, and
                  functionality are owned by SiteVault and are protected by
                  international copyright, trademark, and other intellectual
                  property laws.
                </p>
                <p>
                  You may not copy, modify, create derivative works of, publicly
                  display, or commercially exploit any portion of our Service
                  without our express written permission.
                </p>
                <p>
                  You retain ownership of content you submit. By submitting
                  content, you grant us the license described in Section 4.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-7">
              <Section number="7" title="Third-Party Links">
                <p>
                  Our Service contains links to third-party websites and
                  services. SiteVault has no control over and assumes no
                  responsibility for the content, privacy policies, or practices
                  of any third-party sites.
                </p>
                <p>
                  We strongly advise you to read the terms and privacy policy of
                  any third-party website you visit. Your use of linked sites is
                  at your own risk.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-8">
              <Section number="8" title="Disclaimer of Warranties">
                <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl mb-4">
                  <p className="text-sm font-bold text-amber-800">
                    Important Disclaimer
                  </p>
                </div>
                <p>
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                  WARRANTIES OF ANY KIND. SITEVAULT EXPRESSLY DISCLAIMS ALL
                  WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING:
                </p>
                <div className="space-y-2 mt-3">
                  {[
                    "Implied warranties of merchantability",
                    "Fitness for a particular purpose",
                    "Non-infringement of third-party rights",
                    "Accuracy or completeness of content",
                    "Uninterrupted or error-free operation",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-9">
              <Section number="9" title="Limitation of Liability">
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, SITEVAULT SHALL NOT BE
                  LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                  OR PUNITIVE DAMAGES, INCLUDING:
                </p>
                <div className="space-y-2 mt-3">
                  {[
                    "Loss of profits or revenue",
                    "Loss of data or information",
                    "Business interruption",
                    "Personal injury resulting from service use",
                    "Any unauthorized access to your data",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
                <p className="mt-3">
                  Our total liability to you for any claims arising from these
                  Terms shall not exceed the amount you paid us in the past 12
                  months, or $100 USD, whichever is greater.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-10">
              <Section number="10" title="Termination">
                <p>
                  We may terminate or suspend your account immediately, without
                  prior notice or liability, for any reason, including if you
                  breach these Terms.
                </p>
                <p>
                  Upon termination, your right to use the Service will
                  immediately cease. All provisions of these Terms that should
                  survive termination shall survive.
                </p>
                <p>
                  You may delete your account at any time by contacting us at{" "}
                  <a
                    href="mailto:hello@sitevault.io"
                    className="text-indigo-600 font-bold hover:underline"
                  >
                    hello@sitevault.io
                  </a>
                  .
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-11">
              <Section number="11" title="Governing Law">
                <p>
                  These Terms shall be governed by and construed in accordance
                  with the laws of the State of Delaware, United States, without
                  regard to its conflict of law provisions.
                </p>
                <p>
                  Any disputes arising from these Terms shall be resolved
                  through binding arbitration in accordance with the American
                  Arbitration Association's rules, except where prohibited by
                  law.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-12">
              <Section number="12" title="Changes to Terms">
                <p>
                  We reserve the right to modify these Terms at any time. When
                  we make significant changes, we will notify you by:
                </p>
                <div className="space-y-2 mt-3">
                  {[
                    "Posting the updated Terms on this page",
                    'Updating the "Last updated" date',
                    "Sending an email notification to registered users",
                    "Displaying a notice within the Service",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
                <p className="mt-3">
                  Your continued use of the Service after changes become
                  effective constitutes your acceptance of the revised Terms.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="term-13">
              <Section number="13" title="Contact">
                <p>
                  If you have any questions about these Terms of Service, please
                  contact us:
                </p>
                <div className="mt-4 p-6 bg-white rounded-2xl border-2 border-slate-100 space-y-3">
                  {[
                    {
                      label: "Legal inquiries",
                      value: "legal@sitevault.io",
                      href: "mailto:legal@sitevault.io",
                    },
                    {
                      label: "General support",
                      value: "hello@sitevault.io",
                      href: "mailto:hello@sitevault.io",
                    },
                  ].map((c) => (
                    <div
                      key={c.label}
                      className="flex items-center justify-between gap-4"
                    >
                      <span className="text-sm font-semibold text-slate-500">
                        {c.label}
                      </span>
                      <a
                        href={c.href}
                        className="text-sm font-bold text-indigo-600 hover:underline"
                      >
                        {c.value}
                      </a>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/contact" className="btn-primary">
                    Contact Us <FiArrowRight size={14} />
                  </Link>
                  <Link to="/privacy" className="btn-secondary">
                    View Privacy Policy
                  </Link>
                </div>
              </Section>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
